import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import type { getSpecificationsComponent } from "@/lib/api/formatting/rorosTweed/productPage";

import ProductAccordion from "../ProductAccordion";
import WishlistToggle from "../WishlistToggle";
import styles from "./specifications.module.scss";

type Props = {
  id: string;
  specification: Partial<ReturnType<typeof getSpecificationsComponent>>["data"];
};

const Specification = ({ id, specification }: Props) => {
  const t = useTranslations();
  const { locale } = useRouter();
  const category = specification?.category;
  const composition = specification?.composition;
  const material = specification?.material;
  const size = specification?.size;
  const weight = specification?.weight;
  const productMaterial = specification?.productMaterial;
  const origin = specification?.origin?.find(
    (o) => o.locale === locale || o.locale === "en"
  )?.country;

  const hasMaterialDescription =
    productMaterial && productMaterial.trim().length > 0;

  return (
    <>
      {hasMaterialDescription && (
        <ProductAccordion
          title={t("material")}
          titleExtraComponent={id && <WishlistToggle id={id} />}
        >
          <p className={styles.accordionText}>{productMaterial}</p>
        </ProductAccordion>
      )}
      <ProductAccordion
        title={t("specification")}
        titleExtraComponent={
          id && !hasMaterialDescription && <WishlistToggle id={id} />
        }
      >
        <div className={styles.table}>
          {category && (
            <>
              <p className={styles.title}>{t("category")}</p>
              <Link href={category.categoryUri} className={styles.text}>
                {category.categoryTitle[0]}
              </Link>
            </>
          )}
          {!!composition?.length && (
            <>
              <p className={styles.title}>{t("composition")}</p>
              <div className={styles.text}>
                {composition.map((part) => (
                  <p key={part}>{part}</p>
                ))}
              </div>
            </>
          )}
          {material && (
            <>
              <p className={styles.title}>{t("material")}</p>
              <p className={styles.text}>{material}</p>
            </>
          )}
          {origin && (
            <>
              <p className={styles.title}>{t("madeIn")}</p>
              <p className={styles.text}>{origin}</p>
            </>
          )}
          {size && (
            <>
              <p className={styles.title}>{t("size")}</p>
              <p className={styles.text}>{size}</p>
            </>
          )}
          {weight && (
            <>
              <p className={styles.title}>{t("weight")}</p>
              <p className={styles.text}>{weight}</p>
            </>
          )}
        </div>
      </ProductAccordion>
    </>
  );
};

export default Specification;
